import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/firebase';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { FiUser, FiPhone, FiMail, FiAward, FiArrowLeft } from 'react-icons/fi';

const AddEmployee = () => {
  const [companyData, setCompanyData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    phone: '',
    email: '',
    tier: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const storedCompanyData = localStorage.getItem('companyData');
    if (!storedCompanyData) {
      navigate('/company/login');
      return;
    }

    const company = JSON.parse(storedCompanyData);
    setCompanyData(company);
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? parseInt(value) : value
    });
    setError('');
    setSuccess('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('First name is required.');
      return false;
    }

    if (!formData.surname.trim()) {
      setError('Surname is required.');
      return false;
    }

    if (!formData.phone.trim()) {
      setError('Phone number is required.');
      return false;
    }

    // Basic phone number validation
    const phoneRegex = /^[\+]?[1-9][\d]{3,14}$/;
    if (!phoneRegex.test(formData.phone.replace(/\s+/g, ''))) {
      setError('Please enter a valid phone number.');
      return false;
    }

    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address.');
        return false;
      }
    }

    return true;
  };

  const normalizePhoneNumber = (phone) => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Georgian phone numbers: add +995 if not present
    if (digitsOnly.length === 9) {
      return `+995${digitsOnly}`;
    } else if (digitsOnly.length === 12 && digitsOnly.startsWith('995')) {
      return `+${digitsOnly}`;
    } else if (digitsOnly.startsWith('995') && digitsOnly.length === 12) {
      return `+${digitsOnly}`;
    } else {
      return `+995${digitsOnly}`;
    }
  };

  const checkDuplicateEmployee = async (phone, email) => {
    const normalizedPhone = normalizePhoneNumber(phone);
    
    // Check for duplicate phone number
    const phoneQuery = query(
      collection(db, 'users'),
      where('phone', '==', normalizedPhone)
    );
    const phoneSnapshot = await getDocs(phoneQuery);
    
    if (!phoneSnapshot.empty) {
      throw new Error('An employee with this phone number already exists.');
    }

    // Check for duplicate email (if provided)
    if (email && email.trim()) {
      const emailQuery = query(
        collection(db, 'users'),
        where('email', '==', email.toLowerCase())
      );
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        throw new Error('An employee with this email address already exists.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError('');

    try {
      // Check for duplicates
      await checkDuplicateEmployee(formData.phone, formData.email);

      const normalizedPhone = normalizePhoneNumber(formData.phone);

      const employeeData = {
        name: formData.name.trim(),
        surname: formData.surname.trim(),
        phone: normalizedPhone,
        email: formData.email.trim().toLowerCase() || null,
        tier: formData.tier,
        companyId: companyData.id,
        createdAt: new Date(),
        addedBy: companyData.adminData.email,
        isActive: true
      };

      await addDoc(collection(db, 'users'), employeeData);

      setSuccess(`Employee ${formData.name} ${formData.surname} has been successfully added!`);

      // Reset form
      setFormData({
        name: '',
        surname: '',
        phone: '',
        email: '',
        tier: 1
      });

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/company/employees');
      }, 2000);

    } catch (error) {
      console.error('Error adding employee:', error);
      setError(error.message || 'Failed to add employee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-employee">
      <div className="add-employee-header">
        <button 
          className="back-btn"
          onClick={() => navigate('/company/employees')}
        >
          <FiArrowLeft />
          Back to Employees
        </button>
        <div className="header-title">
          <h1>Add New Employee</h1>
          <p>Add a new employee to {companyData?.name}</p>
        </div>
      </div>

      <div className="add-employee-form-container">
        <form onSubmit={handleSubmit} className="add-employee-form">
          {error && (
            <div className="form-message error">
              {error}
            </div>
          )}

          {success && (
            <div className="form-message success">
              {success}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">
              <FiUser className="label-icon" />
              First name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Mariam"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="surname">
              <FiUser className="label-icon" />
              Surname *
            </label>
            <input
              type="text"
              id="surname"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              placeholder="e.g. Beridze"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">
              <FiPhone className="label-icon" />
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="5XXXXXXXX (9 digits, +995 will be added automatically)"
              required
            />
            <small className="form-hint">
              Enter 9-digit Georgian mobile number (e.g., 555123456). +995 will be added automatically.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="email">
              <FiMail className="label-icon" />
              Email Address (Optional)
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email address"
            />
            <small className="form-hint">
              Optional contact email — not used for sign-in.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="tier">
              <FiAward className="label-icon" />
              Benefit Tier *
            </label>
            <select
              id="tier"
              name="tier"
              value={formData.tier}
              onChange={handleChange}
              required
            >
              <option value={1}>Tier 1 - All Employees</option>
              <option value={2}>Tier 2 - Managers</option>
              <option value={3}>Tier 3 - Executives</option>
            </select>
            <small className="form-hint">
              Tier is based on role level and determines access to benefits
            </small>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => navigate('/company/employees')}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner-sm"></div>
                  Adding Employee...
                </>
              ) : (
                'Add Employee'
              )}
            </button>
          </div>
        </form>

        <div className="add-employee-info">
          <h3>How it works</h3>
          <ul>
            <li>Enter the employee's information above</li>
            <li>The employee signs in to the Loysta app with their phone number</li>
            <li>They confirm with a one-time code (OTP)</li>
            <li>Once verified, they can access benefits based on their tier</li>
          </ul>
          
          <div className="tier-info">
            <h4>Benefit Tiers</h4>
            <div className="tier-descriptions">
              <div className="tier-desc">
                <strong>Tier 1 - All Employees:</strong> General staff, entry-level, standard benefits
              </div>
              <div className="tier-desc">
                <strong>Tier 2 - Managers:</strong> Team leads, supervisors, enhanced benefits
              </div>
              <div className="tier-desc">
                <strong>Tier 3 - Executives:</strong> Senior leadership, C-level, premium benefits
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEmployee;